import { CarbonIntensities } from '@/models/carbon/CarbonIntensity.js'
import { ImpactIntensities } from '@/models/carbon/ImpactIntensity.js'
import CommuteSection from '@/models/carbon/CommuteSection.js'
import Survey from '@/models/carbon/Survey.js'
import Model from '@/models/carbon/Model.js'
import {
  intConverter,
  removeAccents,
  tagsConverter,
  ParsingErrors
} from '@/utils/parser.js'

const ERROR_WORKINGDAY_INVALID = 'error-msg-workingdays-invalid'

export default class Commute extends Survey {
  constructor (options) {
    super(options)
    this.resetSectionsIntensities()
    this.resetSectionsImpacts()
  }

  resetSectionsIntensities () {
    // rationale: initialize the sectionsIntensities (ie intensities by travel mode to a brand new CarbonIntensities)

    // will hold the intensities for each travel mode
    // this is written by getCarbonIntensity and read by toString (avoid to
    // recompute the carbonintensity when exporting)
    this.sectionsIntensities = new Array(Commute.TRAVELS_MODES.length)
      .fill(0)
      .map((i) => new CarbonIntensities())
  }

  resetSectionsImpacts () {
    this.sectionsImpacts = new Array(Commute.TRAVELS_MODES.length)
      .fill(0)
      .map((i) => new ImpactIntensities())
  }

  get hasWorkingDay2 () {
    let hasWorkingDay2 = false
    for (let section of this.sections) {
      if (section.distance > 0 && section.isDay2) {
        hasWorkingDay2 = true
        break
      }
    }
    return hasWorkingDay2
  }

  get isPooling () {
    let isPooling = false
    for (let section of this.sections) {
      if (section.distance > 0 && section.isPooling) {
        isPooling = true
        break
      }
    }
    return isPooling
  }

  get nWorkingDay1 () {
    let nWorkingDay1 = this.nWorkingDay
    if (this.hasWorkingDay2) {
      nWorkingDay1 = this.nWorkingDay - this.nWorkingDay2
    }
    return nWorkingDay1
  }

  get averageWeekDistance () {
    if (this.nWorkingDay > 0) {
      let total = 0
      for (let section of this.sections) {
        if (!section.isDay2) {
          total += this.nWorkingDay1 * parseFloat(section.distance)
        } else {
          total += this.nWorkingDay2 * parseFloat(section.distance)
        }
      }
      return total / this.nWorkingDay
    } else {
      return 0
    }
  }

  get weeklyDistance1PerMode () {
    let distances = new Array(Commute.TRAVELS_MODES.length).fill(0)
    for (let section of this.sections) {
      if (!section.isDay2) {
        let index = Commute.TRAVELS_MODES.indexOf(section.mode)
        distances[index] += parseFloat(section.distance)
      }
    }
    return distances
  }

  get weeklyDistance2PerMode () {
    let distances = new Array(Commute.TRAVELS_MODES.length).fill(0)
    for (let section of this.sections) {
      if (section.isDay2) {
        let index = Commute.TRAVELS_MODES.indexOf(section.mode)
        distances[index] += parseFloat(section.distance)
      }
    }
    return distances
  }

  /**
   * Get number of days per mode
   */
  get commutingDaysPerMode () {
    let cdays = Array(Commute.TRAVELS_MODES.length).fill(0)
    for (let section of this.sections) {
      let index = Commute.TRAVELS_MODES.indexOf(section.mode)
      if (!section.isDay2) {
        cdays[index] += this.nWorkingDay1
      } else {
        cdays[index] += this.nWorkingDay2
      }
    }
    return cdays
  }

  addSection (section) {
    if (section instanceof CommuteSection) {
      this.sections.push(section)
    } else {
      this.sections.push(CommuteSection.createFromObj(section))
    }
  }

  hasMode (mode, isDay2 = false) {
    let index = this.sections.findIndex(
      (obj) => obj.mode === mode && obj.isDay2 === isDay2
    )
    return index !== -1
  }

  hasSections (isDay2 = false) {
    return (
      this.sections.filter((section) => section.isDay2 === isDay2).length > 0
    )
  }

  toString (sep = '\t') {
    let engine = null
    let motorbikepooling = 0
    let carpooling = 0
    let engine2 = null
    let motorbikepooling2 = 0
    let carpooling2 = 0
    for (let section of this.sections) {
      if (!section.isDay2 && section.mode === 'car') {
        engine = section.engine
        carpooling = section.pooling
      } else if (!section.isDay2 && section.mode === 'motorbike') {
        motorbikepooling = section.pooling
      } else if (section.isDay2 && section.mode === 'car') {
        engine2 = section.engine
        carpooling2 = section.pooling
      } else if (section.isDay2 && section.mode === 'motorbike') {
        motorbikepooling2 = section.pooling
      }
    }
    let message = this.message
    if (message) {
      message = message.replace(new RegExp('\\n', 'g'), ' ')
    }
    return [
      this.seqID,
      this.cdeleted,
      this.nWorkingDay,
      this.position,
      message,
      this.tags.map((val) => val.name).join(';'),
      engine,
      motorbikepooling,
      carpooling,
      this.weeklyDistance1PerMode.map((val) => Math.round(val)).join(sep),
      this.nWorkingDay2,
      engine2,
      motorbikepooling2,
      carpooling2,
      this.weeklyDistance2PerMode.map((val) => Math.round(val)).join(sep),
      this.sectionsIntensities
        .map((obj) => Math.round(obj.intensity))
        .join(sep),
      this.sectionsIntensities
        .map((obj) => obj.intensity)
        .reduce(function (a, b) {
          return Math.round(a + b)
        }, 0)
    ].join(sep)
  }

  static getStatus (status, positionDictionary) {
    let modifiedStatus = removeAccents(status, false).toLowerCase()
    if (modifiedStatus in positionDictionary) {
      return positionDictionary[modifiedStatus]
    }
    throw new Error(`Invalid position ${status}`)
  }

  static getMode (value) {
    const PATTERNS = {
      walking: /marche|walking/i,
      bike: /(v.lo|trot.?inette|bike|bicycle|kick scooter)/i,
      ebike: /(v.lo .lectrique|electric bike)/i,
      escooter: /(trot.?inette .lectrique|electric scooter)/i,
      motorbike: /(moto|scooter|motorbike|moped|motor scooter)/i,
      car: /voiture|car/i,
      bus: /bus/i,
      busintercity: /intercity|intercit.|autocar/i,
      tram: /tram|tramway/i,
      train: /train/i,
      expressrailway: /RER|transilien|suburban/i,
      subway: /m.tro|subway/i,
      cablecar: /t.l.cabine|t.l.ph.rique|cable car/i
    }
    let toReturn = ''
    for (let name of Object.keys(PATTERNS)) {
      if (value.search(PATTERNS[name]) >= 0) {
        toReturn = name
      }
    }
    return toReturn
  }

  getAnnualDistancesPerMode (year, options) {
    let distances = new Array(Commute.TRAVELS_MODES.length).fill(0)
    const nbWeeks = this.getNumberOfWorkedWeeks(options, year)
    for (let section of this.sections) {
      let index = Commute.TRAVELS_MODES.indexOf(section.mode)
      if (!section.isDay2) {
        distances[index] += section.distance * this.nWorkingDay1 * nbWeeks
      } else {
        distances[index] += section.distance * this.nWorkingDay2 * nbWeeks
      }
    }
    return distances
  }

  getAnnualDistance (options, year = null) {
    return this.getAnnualDistancesPerMode(year, options).reduce((a, b) => a + b)
  }

  getTotalIntensity (options, year = null, citySize = 0) {
    let intensities = new CarbonIntensities()
    for (let intensity of this.getCarbonIntensity(citySize, options, year)) {
      intensities.add(intensity)
    }
    return intensities.sum()
  }

  getCarbonIntensity (citySize, options, year = null) {
    const nbWeeks = this.getNumberOfWorkedWeeks(options, year)
    this.resetSectionsIntensities()
    for (let section of this.sections) {
      let anualFactor = this.nWorkingDay1 * nbWeeks
      if (section.isDay2) {
        anualFactor = this.nWorkingDay2 * nbWeeks
      }
      let index = Commute.TRAVELS_MODES.indexOf(section.mode)
      this.sectionsIntensities[index].add(
        section.getCarbonIntensity(citySize, year).multiply(anualFactor)
      )
    }
    return this.sectionsIntensities
  }

  getTotalImpact (settings, year = null, citySize = 0) {
    let impacts = new ImpactIntensities()
    for (let intensity of this.getImpact(citySize, settings, year)) {
      impacts.add(intensity)
    }
    return impacts.sum()
  }

  getImpact (citySize, settings, year = null) {
    const nbWeeks = this.getNumberOfWorkedWeeks(settings, year)
    this.resetSectionsImpacts()
    for (let section of this.sections) {
      let anualFactor = this.nWorkingDay1 * nbWeeks
      if (section.isDay2) {
        anualFactor = this.nWorkingDay2 * nbWeeks
      }
      let index = Commute.TRAVELS_MODES.indexOf(section.mode)
      this.sectionsImpacts[index].add(
        section.getImpact(citySize, year).multiply(anualFactor)
      )
    }
    return this.sectionsImpacts
  }

  toDatabase () {
    if (!this.nWorkingDay2) {
      this.nWorkingDay2 = 0
    }
    return {
      id: this.id,
      seqID: this.seqID,
      position: this.position,
      sections: this.sections,
      meals: this.meals,
      nWorkingDay: this.nWorkingDay,
      nWorkingDay2: this.nWorkingDay2,
      nCateringMeal: this.nCateringMeal,
      message: this.message,
      cdeleted: this.cdeleted,
      fromFile: this.fromFile,
      tags: this.tags
    }
  }

  static get TRAVELS_MODES () {
    return [
      'walking',
      'bike',
      'ebike',
      'escooter',
      'motorbike',
      'car',
      'bus',
      'busintercity',
      'tram',
      'train',
      'expressrailway',
      'subway',
      'cablecar'
    ]
  }

  static ICONS (mode) {
    return {
      walking: 'walk',
      bike: 'bicycle',
      ebike: 'electric-bike',
      escooter: 'electric-scooter',
      motorbike: 'motorcycle',
      car: 'car',
      bus: 'bus',
      busintercity: 'bus',
      tram: 'tram',
      train: 'train',
      expressrailway: 'rer',
      subway: 'subway',
      cablecar: 'cablecar'
    }[mode]
  }

  static ICONS_PACK (mode) {
    return {
      walking: 'icomoon',
      bike: 'fa',
      ebike: 'icomoon',
      escooter: 'icomoon',
      motorbike: 'icomoon',
      car: 'fa',
      bus: 'fa',
      busintercity: 'fa',
      tram: 'icomoon',
      train: 'fa',
      expressrailway: 'icomoon',
      subway: 'icomoon',
      cablecar: 'icomoon'
    }[mode]
  }

  static MAX_DISTANCES (mode) {
    return {
      walking: 60,
      bike: 200,
      ebike: 200,
      escooter: 200,
      motorbike: 2000,
      car: 2000,
      bus: 2000,
      busintercity: 2000,
      tram: 300,
      train: 2000,
      expressrailway: 2000,
      subway: 250,
      cablecar: 60
    }[mode]
  }

  static createFromObj (obj) {
    return new Commute(obj)
  }

  static createSection (mode, dist, isday2, pool, engine) {
    let section = new CommuteSection({
      mode: mode,
      distance: dist,
      isDay2: isday2
    })
    if (mode === 'car') {
      section.pooling = pool
      section.engine = engine
    }
    return section
  }

  /**
   * @param {Commute[]} lines - The list of commute object to handle
   * @param {String} format - The file format if exists
   * @param {String} tags - The list of tags defined by the user
   * @param {String} source - The source file name
   *
   * @returns {Commute[]} - The list of commute objects
   */
  static createFromLines (lines, format, tags, source) {
    let commutes = []
    // keep track of some further validation error
    let parsingErrors = new ParsingErrors()
    let lineNumber = 0
    for (let line of lines) {
      // early sanity checks
      lineNumber++
      if (line.nWorkingDay <= line.nWorkingDay2) {
        // record an error
        parsingErrors.add(ERROR_WORKINGDAY_INVALID, {
          row: lineNumber,
          raw: line,
          inName: 'nWorkgingDay'
        })
      }

      let nitem = new Commute({
        id: null,
        seqID: line.seqID,
        position: line.position,
        nWorkingDay: line.nWorkingDay,
        nWorkingDay2: line.nWorkingDay2,
        source: source,
        fromFile: true
      })
      nitem.addTags(Model.getTagsFromNames(line.tags, tags))

      for (const mode of Object.keys(line).filter((key) =>
        key.includes('mode')
      )) {
        if (line[mode]) {
          let isDay2 = false
          let num = mode[4]
          if (mode.includes('Main')) {
            let newSection = Commute.createSection(
              line[mode],
              line['distance' + num + 'Main'],
              isDay2,
              line['carpoolingMain'],
              line['engineMain']
            )
            nitem.addSection(newSection)
          } else {
            isDay2 = true
            let newSection = Commute.createSection(
              line[mode],
              line['distance' + num + 'Day2'],
              isDay2,
              line['carpoolingDay2'],
              line['engineDay2']
            )
            nitem.addSection(newSection)
          }
        }
      }
      commutes.push(nitem)
    }
    if (!parsingErrors.empty) {
      // some lines needs attention
      throw parsingErrors
    }
    return commutes
  }

  static formatDescription (positionDictionary) {
    let positiond = positionDictionary
    return {
      seqID: {
        pattern: /^id$|^n°$|^N°$|^Num$/i,
        required: true
      },
      position: {
        pattern: /(staff category|staff|statut)/i,
        required: true,
        converter: function (value) {
          return Commute.getStatus(value, positiond)
        }
      },
      nWorkingDay: {
        pattern: /(working days|jours? de travail|jours? travaill.)/i,
        required: true,
        converter: intConverter,
        default: 0
      },
      mode1Main: {
        pattern: /(mode 1 j1|mode 1 day 1)/i,
        required: true,
        converter: function (value) {
          return Commute.getMode(value)
        },
        default: ''
      },
      mode2Main: {
        pattern: /(mode 2 j1|mode 2 day 1)/i,
        required: false,
        converter: function (value) {
          return Commute.getMode(value)
        },
        default: ''
      },
      mode3Main: {
        pattern: /(mode 3 j1|mode 3 day 1)/i,
        required: false,
        converter: function (value) {
          return Commute.getMode(value)
        },
        default: ''
      },
      distance1Main: {
        pattern: /(distance mode 1 j1|distance mode 1 day 1)/i,
        converter: intConverter,
        required: true,
        default: 0
      },
      distance2Main: {
        pattern: /(distance mode 2 j1|distance mode 2 day 1)/i,
        converter: intConverter,
        required: false,
        default: 0
      },
      distance3Main: {
        pattern: /(distance mode 3 j1|distance mode 3 day 1)/i,
        converter: intConverter,
        required: false,
        default: 0
      },
      engineMain: {
        pattern: /(motorisation.* j1|engine.* day 1)/i,
        required: false,
        converter: function (value) {
          const PATTERNS = {
            gasoline: /essence|gasoline/i,
            diesel: /diesel|gazole/i,
            'unknown.engine': /inconnue|inconnu|unknown/i,
            lpg: /GPL|LPG/i,
            cng: /GNV|CNG/i,
            e85: /E85/i,
            hybrid: /hybride|hybrid/i,
            electric: /.lectrique|electric/i
          }
          let toReturn = ''
          for (let name of Object.keys(PATTERNS)) {
            if (value.search(PATTERNS[name]) >= 0) {
              toReturn = name
            }
          }
          return toReturn
        },
        default: ''
      },
      carpoolingMain: {
        pattern:
          /(covoiturage.* j1|Nb.* pers.* voiture.* j1|carpooling day 1|Nb.* people .*car.* day 1)/i,
        default: 1,
        required: false,
        converter: intConverter
      },
      nWorkingDay2: {
        pattern: /(Nb.* jours.* j2|Nb.* days typical.day.*2)/i,
        required: false,
        converter: intConverter,
        default: 0
      },
      mode1Day2: {
        pattern: /(mode 1 j2|mode 1 day 2)/i,
        required: false,
        converter: function (value) {
          return Commute.getMode(value)
        },
        default: ''
      },
      mode2Day2: {
        pattern: /(mode 2 j2|mode 2 day 2)/i,
        required: false,
        converter: function (value) {
          return Commute.getMode(value)
        },
        default: ''
      },
      mode3Day2: {
        pattern: /(mode 3 j2|mode 3 day 2)/i,
        required: false,
        converter: function (value) {
          return Commute.getMode(value)
        },
        default: ''
      },
      distance1Day2: {
        pattern: /(distance mode 1 j2|distance mode 1 day 2)/i,
        converter: intConverter,
        required: false,
        default: 0
      },
      distance2Day2: {
        pattern: /(distance mode 2 j2|distance mode 2 day 2)/i,
        converter: intConverter,
        required: false,
        default: 0
      },
      distance3Day2: {
        pattern: /(distance mode 3 j2|distance mode 3 day 2)/i,
        converter: intConverter,
        required: false,
        default: 0
      },
      engineDay2: {
        pattern: /(motorisation j2|engine day 2)/i,
        required: false,
        converter: function (value) {
          const PATTERNS = {
            gasoline: /essence|gasoline/i,
            diesel: /diesel|gazole/i,
            'unknown.engine': /inconnue|inconnu|unknown/i,
            lpg: /GPL|LPG/i,
            cng: /GNV|CNG/i,
            e85: /E85/i,
            hybrid: /hybride|hybrid/i,
            electric: /.lectrique|electric/i
          }
          let toReturn = ''
          for (let name of Object.keys(PATTERNS)) {
            if (value.search(PATTERNS[name]) >= 0) {
              toReturn = name
            }
          }
          return toReturn
        },
        default: ''
      },
      carpoolingDay2: {
        pattern:
          /(covoiturage j2|Nb.* pers.* voiture.* j2|carpooling day 2|Nb.* people .*car.* day 2)/i,
        default: 1,
        required: false,
        converter: intConverter
      },
      tags: {
        pattern: /(^tag$|^tags$|^label$|^labels$)/i,
        converter: tagsConverter,
        default: []
      }
    }
  }

  static createEmpty () {
    return new Commute()
  }

  static initiStatusTable (
    positionNames,
    length,
    fillIntensities = false,
    constructor = CarbonIntensities
  ) {
    let table = {}
    for (let positionName of positionNames) {
      table[positionName] = new Array(length).fill(0)
    }
    if (fillIntensities) {
      for (let key of Object.keys(table)) {
        for (let i in table[key]) {
          table[key][i] = new constructor()
        }
      }
      return table
    } else {
      return table
    }
  }

  static exportHeader (sep = '\t') {
    let header = [
      'seqid',
      'deleted',
      'commuting.days',
      'position',
      'message',
      'tags',
      'engine.std1',
      'motorbikepooling.std1',
      'carpooling.std1'
    ]
    for (let mode of Commute.TRAVELS_MODES) {
      header.push(mode + '.std1.km')
    }
    header.push('commuting.std2.days')
    header.push('engine.std2')
    header.push('motorbikepooling.std2')
    header.push('carpooling.std2')
    for (let mode of Commute.TRAVELS_MODES) {
      header.push(mode + '.std2.km')
    }
    for (let mode of Commute.TRAVELS_MODES) {
      header.push(mode + '.kg.co2e')
    }
    header.push('total.kg.co2e')
    return header.join(sep)
  }

  static exportToFile (items, header = true, extraColValue = null, sep = '\t') {
    let headerValues = []
    if (header) {
      headerValues = Commute.exportHeader(sep)
    }
    return [
      headerValues,
      ...items.map(function (item) {
        let val = ''
        if (extraColValue) {
          val += extraColValue + sep
        }
        return val + item.toString(sep)
      })
    ]
      .join('\n')
      .replace(/(^\[)|(\]$)/gm, '')
  }

  static compute (items, citySize, boundary, year, options, tags = []) {
    let surveyStructure = boundary.surveyStructure
    let intensities = Commute.initiStatusTable(
      surveyStructure.names,
      Commute.TRAVELS_MODES.length,
      true
    )
    let impacts = Commute.initiStatusTable(
      surveyStructure.names,
      Commute.TRAVELS_MODES.length,
      true,
      ImpactIntensities
    )
    let distances = Commute.initiStatusTable(
      surveyStructure.names,
      Commute.TRAVELS_MODES.length
    )
    // hold the cumulative number of day each travel mode is used indexed by the position
    let volumePerMode = Commute.initiStatusTable(
      surveyStructure.names,
      Commute.TRAVELS_MODES.length
    )
    let cdays = {}
    let nbAnswers = {}
    let nbAnswersTag = {}
    for (let position of surveyStructure) {
      cdays[position.name] = 0
      nbAnswers[position.name] = 0
      nbAnswersTag[position.name] = 0
    }
    // FIXME(msimonin): handle the impossoble:
    // item.position isn't part of the surveyStructure
    // - count it somewhere else ?
    let totalPerCommute = []
    for (let item of items) {
      let intensity = item.getCarbonIntensity(citySize, options, year)
      let impact = item.getImpact(citySize, options, year)
      if (!item.cdeleted) {
        nbAnswers[item.position] += 1
        if (item.hasTags(tags)) {
          let cDistances = item.getAnnualDistancesPerMode(year, options)
          let nDaysPermode = item.commutingDaysPerMode
          totalPerCommute.unshift([item.seqID].concat(intensity))
          nbAnswersTag[item.position] += 1
          cdays[item.position] +=
            item.nWorkingDay * item.getNumberOfWorkedWeeks(options, year)
          for (let index = 0; index < Commute.TRAVELS_MODES.length; index++) {
            intensities[item.position][index].add(intensity[index])
            impacts[item.position][index].add(impact[index])
            distances[item.position][index] += cDistances[index]
            volumePerMode[item.position][index] +=
              nDaysPermode[index] * item.getNumberOfWorkedWeeks(options, year)
          }
        }
      }
    }
    // convert CarbonIntensities to CarbonIntensity for normalization
    Object.keys(intensities).map(function (key) {
      intensities[key] = intensities[key].map((obj) => obj.sum())
    })
    Object.keys(impacts).map(function (key) {
      impacts[key] = impacts[key].map((obj) => obj.sum())
    })
    // normalize intensity by the number of lab members
    for (let position of Object.keys(intensities)) {
      for (let index = 0; index < Commute.TRAVELS_MODES.length; index++) {
        let normalizationFactor = 0
        if (nbAnswers[position] !== 0) {
          normalizationFactor = boundary.positionNormalizationFactor(
            position,
            nbAnswers[position]
          )
        }
        intensities[position][index] =
          intensities[position][index].multiply(normalizationFactor)
        impacts[position][index] =
          impacts[position][index].multiply(normalizationFactor)
        distances[position][index] =
          distances[position][index] * normalizationFactor
        volumePerMode[position][index] =
          volumePerMode[position][index] * normalizationFactor
      }
    }
    let total = new CarbonIntensities()
    for (let position of Object.keys(intensities)) {
      for (let index = 0; index < Commute.TRAVELS_MODES.length; index++) {
        total.add(intensities[position][index])
      }
    }
    let totali = new ImpactIntensities()
    for (let position of Object.keys(impacts)) {
      for (let index = 0; index < Commute.TRAVELS_MODES.length; index++) {
        totali.add(impacts[position][index])
      }
    }
    return {
      intensity: total.sum(),
      impact: totali.sum(),
      totalPerCommute: totalPerCommute,
      status: {
        intensity: intensities,
        impact: impacts,
        distances: distances,
        volumePerMode: volumePerMode
      },
      nbAnswers: nbAnswers,
      cdays: cdays
    }
  }

  /**
   * Aggregate the previously computed emissions
   * used when exporting the data for anonymous analysis
   *
   * The input parameter is the returned object of the compute function
   *
   */
  static aggregate (emissions) {
    // the object that will be returned
    let dict = {}
    // handle per position number of answers
    for (let [k, v] of Object.entries(emissions.nbAnswers)) {
      dict[`no.answers.${k}`] = v
    }
    // handle per position number of commuting days
    for (let [k, v] of Object.entries(emissions.cdays)) {
      dict[`commuting.days.${k}`] = v
    }
    // handle per position (per mode distances)
    for (let [k, v] of Object.entries(emissions.status.distances)) {
      // zip v with Commutes.TRAVEL_MODES
      for (let i in Commute.TRAVELS_MODES) {
        let mode = Commute.TRAVELS_MODES[i]
        let value = v[i]
        dict[`${mode}.km.${k}`] = value
      }
    }
    // handle per position (per mode emissions)
    for (let [k, v] of Object.entries(emissions.status.intensity)) {
      // zip v with Commutes.TRAVEL_MODES
      for (let i in Commute.TRAVELS_MODES) {
        let mode = Commute.TRAVELS_MODES[i]
        let intensity = v[i].intensity
        let uncertainty = v[i].uncertainty
        dict[`${mode}.emission.kg.co2e.${k}`] = intensity
        dict[`${mode}.uncertainty.kg.co2e.${k}`] = uncertainty
      }
    }
    dict['emission.kg.co2e'] = emissions.intensity.intensity
    dict['uncertainty.kg.co2e'] = emissions.intensity.uncertainty

    return dict
  }
}
